import {
  BuyableItemPricingBreakdown,
  ComputablePricing,
  PartialBuyableItem,
  SimpleBasket,
} from "@ac-expo/api-types";
import { BuyableItemType, DiscountType } from "@prisma/client";
import { getBuyableItemGroupKey, isValidBuyableItem } from "../buyable-items";
import { computeRefundBaseTotalsFromCreditNoteItems, getExcludedTaxesPrice } from "../prices";
import {
  compareGroupKeys,
  computeItemPrice,
  createDiscountCodeBudget,
  DiscountCodeBudget,
} from "./pricing";

export interface DiscountCodeToApply {
  id: string;
  code: string;
  discount: {
    type: "percentage" | "euro";
    value: number;
  };
  // Units this code may still discount in the basket (total and per-visitor limits combined).
  // Missing or null: unlimited.
  maxApplications?: number | null;
}

export interface BuyableItemGroupWithPricing {
  items: PartialBuyableItem[];
  pricing: ComputablePricing;
}

export interface BasketPricingInput {
  groupedItems: BuyableItemGroupWithPricing[];
  discountCodesToApply: DiscountCodeToApply[];
}

type NewComputablePricing = Omit<ComputablePricing, "id">;

type AppliedDiscountDetail = {
  id: string;
  discountValue: number;
  discountType: DiscountType;
  amount: number;
};

export interface BasketGroupItem {
  item: PartialBuyableItem;
  basePricing: ComputablePricing;
  pricing: NewComputablePricing;
  finalPrice: number;
  codeDiscountAmount: number;
  degressiveDiscountAmount: number;
  appliedDiscountDetails: AppliedDiscountDetail[];
  appliedDegressivePrice: ComputablePricing["degressivePrices"][0] | null;
  degressiveTriggerQuantity: number | null;
}

export interface BasketPricingGroup {
  items: BasketGroupItem[];
  codeDiscount: number;
  exclTaxesCodeDiscount: number;
  degressiveDiscount: number;
  exclTaxesDegressiveDiscount: number;
  finalPrice: number;
  exclTaxesTotal: number;
  basePrice: number;
  baseExclTaxesPrice: number;
}

export interface BasketPricingResult {
  groups: BasketPricingGroup[];
  // Units actually discounted by each applied code, and eligible units it had to leave at full
  // price once its budget ran out, keyed by code id.
  appliedUnitsByCode: Record<string, number>;
  skippedUnitsByCode: Record<string, number>;
  codeDiscount: number;
  exclTaxesCodeDiscount: number;
  degressiveDiscount: number;
  exclTaxesDegressiveDiscount: number;
  finalPrice: number;
  exclTaxesTotal: number;
  basePrice: number;
  baseExclTaxesPrice: number;
}

interface ProcessedItem {
  item: PartialBuyableItem;
  pricing: NewComputablePricing;
  basePrice: number;
  finalPrice: number;
  appliedDiscountDetails: AppliedDiscountDetail[];
  codeDiscount: number;
  degressiveDiscount: number;
  baseExclTaxesPrice: number;
  finalPriceExclTaxes: number;
  codeDiscountExclTaxes: number;
  degressiveDiscountExclTaxes: number;
  appliedDegressivePrice: ComputablePricing["degressivePrices"][0] | null;
  degressiveTriggerQuantity: number | null;
}

interface GroupTotals {
  codeDiscount: number;
  exclTaxesCodeDiscount: number;
  degressiveDiscount: number;
  exclTaxesDegressiveDiscount: number;
  finalPrice: number;
  exclTaxesTotal: number;
  basePrice: number;
  baseExclTaxesPrice: number;
}

const createItemPricingTraceability = (
  originalPricing: ComputablePricing,
  appliedDegressivePrice: ComputablePricing["degressivePrices"][0] | null,
  appliedDiscountCodes: ComputablePricing["discountCodes"],
  finalPrice: number,
  vatRate: number,
): NewComputablePricing => {
  return {
    isPaid: originalPricing.isPaid,
    price: {
      priceTTC: finalPrice,
      vatRate,
    },
    degressivePrices: appliedDegressivePrice ? [appliedDegressivePrice] : [],
    discountCodes: appliedDiscountCodes,
    ticketing: originalPricing.ticketing,
  };
};

const getRefundPricingDetails = (item: PartialBuyableItem) => {
  if (item.buyableType !== BuyableItemType.refund) return null;

  const creditNoteItems = item.creditNote?.creditNoteItems ?? [];
  return computeRefundBaseTotalsFromCreditNoteItems(creditNoteItems);
};

const processIndividualItem = (
  item: PartialBuyableItem,
  position: number,
  groupPricing: ComputablePricing,
  discountCodesToApply: DiscountCodeToApply[],
  budget: DiscountCodeBudget,
): ProcessedItem => {
  const {
    finalPrice,
    codeDiscountAmount,
    appliedDegressivePrice,
    appliedDiscountCodes,
    degressiveDiscountAmount,
    basePrice,
    vatRate,
  } = computeItemPrice(groupPricing, position, discountCodesToApply, budget);

  const refundPricingDetails = getRefundPricingDetails(item);

  const appliedDiscountDetails: AppliedDiscountDetail[] = appliedDiscountCodes.map(
    (appliedCode) => ({
      id: appliedCode.discountCode.id,
      discountValue: appliedCode.discountCode.discount.value,
      discountType: appliedCode.discountCode.discount.type,
      amount: Math.round(appliedCode.discountAmount),
    }),
  );

  let vatRateForTraceability = vatRate;
  let codeDiscountExclTaxes = getExcludedTaxesPrice(codeDiscountAmount, vatRate);
  let degressiveDiscountExclTaxes = getExcludedTaxesPrice(degressiveDiscountAmount, vatRate);
  let baseExclTaxesPrice = getExcludedTaxesPrice(basePrice, vatRate);
  let finalPriceExclTaxes = getExcludedTaxesPrice(finalPrice, vatRate);

  if (refundPricingDetails) {
    const ratio = refundPricingDetails.baseHt / refundPricingDetails.baseTtc;
    baseExclTaxesPrice = basePrice * ratio;
    finalPriceExclTaxes = finalPrice * ratio;
    codeDiscountExclTaxes = codeDiscountAmount * ratio;
    degressiveDiscountExclTaxes = degressiveDiscountAmount * ratio;
    vatRateForTraceability =
      finalPriceExclTaxes > 0
        ? Math.round((finalPrice / finalPriceExclTaxes - 1) * 10000)
        : vatRateForTraceability;
  }

  const itemPricing = createItemPricingTraceability(
    groupPricing,
    appliedDegressivePrice,
    appliedDiscountCodes,
    finalPrice,
    vatRateForTraceability,
  );

  return {
    item,
    pricing: itemPricing,
    basePrice,
    appliedDiscountDetails,
    finalPrice,
    codeDiscount: codeDiscountAmount,
    degressiveDiscount: degressiveDiscountAmount,
    baseExclTaxesPrice,
    finalPriceExclTaxes,
    codeDiscountExclTaxes,
    degressiveDiscountExclTaxes,
    appliedDegressivePrice,
    degressiveTriggerQuantity:
      appliedDegressivePrice && degressiveDiscountAmount > 0 ? position : null,
  };
};

const processFreeItemsGroup = (
  items: PartialBuyableItem[],
  pricing: ComputablePricing,
): BasketGroupItem[] => {
  return items.map((item) => ({
    item,
    basePricing: pricing,
    pricing: {
      ...pricing,
      price: { priceTTC: 0, vatRate: 0 },
    },
    finalPrice: 0,
    basePrice: 0,
    baseExclTaxesPrice: 0,
    codeDiscountAmount: 0,
    degressiveDiscountAmount: 0,
    appliedDiscountDetails: [],
    appliedDegressivePrice: null,
    degressiveTriggerQuantity: null,
  }));
};

const processPaidItemsGroup = (
  items: PartialBuyableItem[],
  pricing: ComputablePricing,
  discountCodesToApply: DiscountCodeToApply[],
  budget: DiscountCodeBudget,
): { groupItems: BasketGroupItem[]; totals: GroupTotals } => {
  const processedItems = items.map((item, index) =>
    processIndividualItem(item, index + 1, pricing, discountCodesToApply, budget),
  );

  const groupItems: BasketGroupItem[] = processedItems.map((processedItem) => ({
    item: processedItem.item,
    basePricing: pricing,
    pricing: processedItem.pricing,
    finalPrice: processedItem.finalPrice,
    codeDiscountAmount: Math.round(processedItem.codeDiscount),
    degressiveDiscountAmount: Math.round(processedItem.degressiveDiscount),
    appliedDiscountDetails: processedItem.appliedDiscountDetails,
    appliedDegressivePrice: processedItem.appliedDegressivePrice,
    degressiveTriggerQuantity: processedItem.degressiveTriggerQuantity,
  }));

  const totals = aggregateGroupTotals(processedItems);

  return { groupItems, totals };
};

const aggregateGroupTotals = (processedItems: ProcessedItem[]): GroupTotals => {
  return processedItems.reduce(
    (acc, item) => ({
      codeDiscount: acc.codeDiscount + item.codeDiscount,
      exclTaxesCodeDiscount: acc.exclTaxesCodeDiscount + item.codeDiscountExclTaxes,
      degressiveDiscount: acc.degressiveDiscount + item.degressiveDiscount,
      exclTaxesDegressiveDiscount:
        acc.exclTaxesDegressiveDiscount + item.degressiveDiscountExclTaxes,
      finalPrice: acc.finalPrice + item.finalPrice,
      exclTaxesTotal: acc.exclTaxesTotal + item.finalPriceExclTaxes,
      basePrice: acc.basePrice + item.basePrice,
      baseExclTaxesPrice: acc.baseExclTaxesPrice + item.baseExclTaxesPrice,
    }),
    {
      codeDiscount: 0,
      exclTaxesCodeDiscount: 0,
      degressiveDiscount: 0,
      exclTaxesDegressiveDiscount: 0,
      finalPrice: 0,
      exclTaxesTotal: 0,
      basePrice: 0,
      baseExclTaxesPrice: 0,
    },
  );
};

const aggregateFinalTotals = (
  groups: BasketPricingGroup[],
): Omit<BasketPricingResult, "groups" | "appliedUnitsByCode" | "skippedUnitsByCode"> => {
  return groups.reduce(
    (acc, group) => ({
      codeDiscount: acc.codeDiscount + group.codeDiscount,
      exclTaxesCodeDiscount: acc.exclTaxesCodeDiscount + group.exclTaxesCodeDiscount,
      degressiveDiscount: acc.degressiveDiscount + group.degressiveDiscount,
      exclTaxesDegressiveDiscount:
        acc.exclTaxesDegressiveDiscount + group.exclTaxesDegressiveDiscount,
      finalPrice: acc.finalPrice + group.finalPrice,
      exclTaxesTotal: acc.exclTaxesTotal + group.exclTaxesTotal,
      basePrice: acc.basePrice + group.basePrice,
      baseExclTaxesPrice: acc.baseExclTaxesPrice + group.baseExclTaxesPrice,
    }),
    {
      codeDiscount: 0,
      exclTaxesCodeDiscount: 0,
      degressiveDiscount: 0,
      exclTaxesDegressiveDiscount: 0,
      finalPrice: 0,
      exclTaxesTotal: 0,
      basePrice: 0,
      baseExclTaxesPrice: 0,
    },
  );
};

const processItemGroup = (
  group: BuyableItemGroupWithPricing,
  discountCodesToApply: DiscountCodeToApply[],
  budget: DiscountCodeBudget,
): BasketPricingGroup => {
  const { items, pricing } = group;

  if (!pricing.price) {
    const groupItems = processFreeItemsGroup(items, pricing);
    return {
      items: groupItems,
      codeDiscount: 0,
      exclTaxesCodeDiscount: 0,
      degressiveDiscount: 0,
      exclTaxesDegressiveDiscount: 0,
      finalPrice: 0,
      exclTaxesTotal: 0,
      basePrice: 0,
      baseExclTaxesPrice: 0,
    };
  }

  const { groupItems, totals } = processPaidItemsGroup(
    items,
    pricing,
    discountCodesToApply,
    budget,
  );

  return {
    items: groupItems,
    ...totals,
  };
};

const groupAllocationKey = (group: BuyableItemGroupWithPricing): string => {
  const [first] = group.items;
  return first && isValidBuyableItem(first) ? getBuyableItemGroupKey(first) : "";
};

const countAppliedUnitsByCode = (groups: BasketPricingGroup[]): Record<string, number> => {
  const counts: Record<string, number> = {};
  for (const group of groups) {
    for (const item of group.items) {
      for (const { id } of item.appliedDiscountDetails) {
        counts[id] = (counts[id] ?? 0) + 1;
      }
    }
  }
  return counts;
};

export const calculateBasketItemsPricing = (
  groupedItems: BuyableItemGroupWithPricing[],
  discountCodesToApply: DiscountCodeToApply[],
): BasketPricingResult => {
  // Capped codes are allocated to groups in buyable item group key order, the order the
  // local cart prices its lines in, so both sides discount the same units.
  const budget = createDiscountCodeBudget(discountCodesToApply);
  const processedGroups: BasketPricingGroup[] = new Array(groupedItems.length);
  const allocationOrder = groupedItems.map((group, index) => ({
    index,
    key: budget.remaining.size > 0 ? groupAllocationKey(group) : "",
  }));
  allocationOrder.sort((a, b) => compareGroupKeys(a.key, b.key));
  for (const { index } of allocationOrder) {
    processedGroups[index] = processItemGroup(groupedItems[index], discountCodesToApply, budget);
  }

  const finalTotals = aggregateFinalTotals(processedGroups);

  return {
    groups: processedGroups,
    appliedUnitsByCode: countAppliedUnitsByCode(processedGroups),
    skippedUnitsByCode: Object.fromEntries(budget.skipped),
    ...finalTotals,
  };
};

const createItemPricingBreakdown = (
  basketItem: SimpleBasket["basketItem"][0],
  groupItem: BasketGroupItem,
  discountCodeMap: Map<string, SimpleBasket["discountCode"][number]>,
): BuyableItemPricingBreakdown => {
  const pricing = groupItem.pricing;

  const basePrice = groupItem.basePricing.price?.priceTTC ?? 0;
  const vatRate = pricing.price?.vatRate ?? 0;
  const codeDiscount = groupItem.codeDiscountAmount;
  const degressiveDiscount = groupItem.degressiveDiscountAmount;

  return {
    id: basketItem.id,
    buyableItem: basketItem.buyableItem,
    expirationCountdown: basketItem.expirationCountdown,
    createdAt: basketItem.createdAt,
    updatedAt: basketItem.updatedAt,
    basePricing: groupItem.basePricing,
    basePrice,
    vatRate,
    finalPrice: groupItem.finalPrice,
    codeDiscount,
    degressiveDiscount,
    appliedDegressivePrice:
      groupItem.appliedDegressivePrice && degressiveDiscount > 0
        ? {
            id: groupItem.appliedDegressivePrice.id,
            minQuantity: groupItem.appliedDegressivePrice.minQuantity ?? null,
            maxQuantity: groupItem.appliedDegressivePrice.maxQuantity ?? null,
            discountType: groupItem.appliedDegressivePrice.discount.type,
            discountValue: groupItem.appliedDegressivePrice.discount.value,
            triggeredQuantity:
              groupItem.degressiveTriggerQuantity ??
              groupItem.appliedDegressivePrice.minQuantity ??
              1,
          }
        : null,
    appliedCode: groupItem.appliedDiscountDetails.map(({ id, discountValue, amount }) => {
      const discountInfo = discountCodeMap.get(id);
      if (!discountInfo) {
        throw new Error(`Discount code not found for id: ${id}`);
      }

      const discountPrestations = discountInfo.pricings.flatMap(
        ({ pricing: { backofficePrestation }, quantityLeft }) =>
          backofficePrestation ? [{ prestationType: backofficePrestation.type, quantityLeft }] : [],
      );

      return {
        id,
        codeName: discountInfo.name,
        discountType: discountInfo.discount.type,
        discountValue,
        appliedAmount: amount,
        isCumulable: discountInfo.isCumulable,
        quantityLeft: discountInfo.quantityLeft,
        discountPrestations,
      };
    }),
  };
};

export const transformPricingResultToItemBreakdowns = (
  pricingResult: BasketPricingResult,
  basket: SimpleBasket,
): BuyableItemPricingBreakdown[] => {
  const discountCodeMap = new Map(basket.discountCode.map((code) => [code.id, code]));

  const basketItemsByGroupKey = new Map<string, SimpleBasket["basketItem"]>();
  for (const basketItem of basket.basketItem) {
    const groupKey = getBuyableItemGroupKey(basketItem.buyableItem);
    const existing = basketItemsByGroupKey.get(groupKey) ?? [];
    existing.push(basketItem);
    basketItemsByGroupKey.set(groupKey, existing);
  }

  const allGroupItems = pricingResult.groups.flatMap((group) => group.items);

  return allGroupItems.map((groupItem) => {
    const groupKey = getBuyableItemGroupKey(groupItem.item);
    const matchingItems = basketItemsByGroupKey.get(groupKey);

    if (!matchingItems || matchingItems.length === 0) {
      throw new Error(
        `Error while processing item in group: Basket item not found for buyable item ${groupItem.item.buyableType}. ` +
          `Verify that the item exists in the basket. Item type: ${groupItem.item.buyableType}`,
      );
    }

    const basketItem = matchingItems.shift()!; // Warning: order matters, we assume items are processed in the same order

    return createItemPricingBreakdown(basketItem, groupItem, discountCodeMap);
  });
};
